import { useCallback, useEffect, useState } from "react";
import {
  listServiceRigs,
  listServiceWorkQueues,
  upsertServiceWorkQueue,
} from "@/lib/service/client";
import type { ServiceRig, ServiceWorkQueue } from "@/lib/service/types";

const APPROVER_ROLES = ["service_admin", "admin", "owner", "manager"] as const;

const BLANK_DRAFT = {
  label: "",
  slug: "",
  default_rig_id: "",
  default_duration_min: "",
  default_approver_role: "service_admin",
  auto_approve_manufacturing: false,
  photos_required: false,
};

export function useServiceWorkQueuesPanel(navTick: number) {
  const [queues, setQueues] = useState<ServiceWorkQueue[]>([]);
  const [rigs, setRigs] = useState<ServiceRig[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [createDraftOpen, setCreateDraftOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK_DRAFT);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [queueRes, rigRes] = await Promise.all([
      listServiceWorkQueues({ active_only: false }),
      listServiceRigs({ active_only: true }),
    ]);
    setListBusy(false);
    if (!queueRes.ok) {
      setLocalError(queueRes.error);
      return;
    }
    setQueues(queueRes.data.queues);
    setRigs(rigRes.ok ? rigRes.data.rigs : []);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const startCreateDraft = useCallback(() => {
    setLocalError(null);
    setDraft(BLANK_DRAFT);
    setCreateDraftOpen(true);
  }, []);

  const cancelCreateDraft = useCallback(() => {
    setCreateDraftOpen(false);
    setDraft(BLANK_DRAFT);
    setLocalError(null);
  }, []);

  const isCreateDraftValid = draft.label.trim().length > 0;

  const saveQueue = useCallback(async (): Promise<boolean> => {
    if (!isCreateDraftValid) {
      setLocalError("Label is required.");
      return false;
    }
    setActionBusy(true);
    setLocalError(null);
    const durationRaw = draft.default_duration_min.trim();
    const duration = durationRaw ? Number(durationRaw) : null;
    const res = await upsertServiceWorkQueue({
      label: draft.label.trim(),
      slug: draft.slug.trim() || undefined,
      default_rig_id: draft.default_rig_id || null,
      default_duration_min: duration != null && Number.isFinite(duration) ? duration : null,
      default_approver_role: draft.default_approver_role,
      auto_approve_manufacturing: draft.auto_approve_manufacturing,
      photos_required: draft.photos_required,
      is_active: true,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return false;
    }
    setCreateDraftOpen(false);
    setDraft(BLANK_DRAFT);
    await reload();
    return true;
  }, [draft, isCreateDraftValid, reload]);

  const updateQueue = useCallback(
    async (queue: ServiceWorkQueue, patch: Partial<ServiceWorkQueue>) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await upsertServiceWorkQueue({
        id: queue.id,
        label: patch.label ?? queue.label,
        slug: patch.slug ?? queue.slug,
        default_rig_id: patch.default_rig_id !== undefined ? patch.default_rig_id : queue.default_rig_id,
        default_duration_min:
          patch.default_duration_min !== undefined
            ? patch.default_duration_min
            : queue.default_duration_min,
        default_approver_role: patch.default_approver_role ?? queue.default_approver_role,
        auto_approve_manufacturing:
          patch.auto_approve_manufacturing !== undefined
            ? patch.auto_approve_manufacturing
            : queue.auto_approve_manufacturing,
        photos_required:
          patch.photos_required !== undefined ? patch.photos_required : queue.photos_required,
        is_active: patch.is_active !== undefined ? patch.is_active : queue.is_active,
        sort_order: patch.sort_order ?? queue.sort_order,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return false;
      }
      await reload();
      return true;
    },
    [reload]
  );

  return {
    queues,
    rigs,
    listBusy,
    actionBusy,
    localError,
    createDraftOpen,
    startCreateDraft,
    cancelCreateDraft,
    isCreateDraftValid,
    draft,
    setDraft,
    saveQueue,
    updateQueue,
    approverRoles: APPROVER_ROLES,
    reload,
  };
}
